/**
 * Ground that changes the tower standing on it (playtest request).
 *
 * Boards were one clump, and correctly so: support auras reach 2.5–3.2 tiles while
 * weapons reach 3–6, so the binding constraint on placement was "am I inside the bubble"
 * rather than "can I hit the path", and class synergies are global counts with no
 * positional term to pull the other way. One right answer per map, taken by every tower.
 *
 * A tile holds exactly one tower where an aura can be shared by ten, and that exclusivity
 * is the entire mechanism — scattered tiles turn one global optimum into several local
 * ones, which no aura radius can do.
 *
 * ── What is deliberately NOT here ─────────────────────────────────────────────────────
 * Flat damage and attack speed. A multiplier pays out hardest on whatever already hits
 * hardest, so a "+8% damage" tile makes the dominant tower more dominant — it feeds the
 * concentration this feature exists to break. Every effect below is either situational
 * (a class the run may or may not want), positional (range), or non-combat (Core HP).
 */
import type { ClassId } from '@prism/sim';

export interface BuffTileDef {
  id: string;
  name: string;
  /** One line, shown on hover and in the tower inspector. */
  description: string;
  /** Draw colour for the tile, its ring, and the particles it and its tower emit. */
  color: string;
  /**
   * Particle preset the tile and its occupant give off, chosen to READ as the effect:
   * calm rising motes for a class grant, thin reaching sparks for range, falling fire for
   * the ember bed. The tower carries the same one, so a buffed tower is identifiable from
   * the board without selecting it.
   */
  particle: 'zoneMote' | 'beamMote' | 'ember';
  /** Relative draw weight in the generator. */
  weight: number;

  // ── Mechanics, passed through to the sim verbatim ──
  grantsClass?: ClassId;
  rangeBonus?: number;
  onHitStatus?: { status: string; magnitude: number; durationTicks: number };
  coreHpEveryNWaves?: number;
}

/**
 * One class-granting tile per class.
 *
 * The strongest effect in the set, because its worth is situational rather than flat:
 * worthless when the run holds none of that class, run-defining at five. That it changes
 * value every run — not just every map — is what makes it a decision rather than a pickup.
 */
const CLASS_TILES: BuffTileDef[] = (
  [
    ['warden', 'Bulwark Stone', '#8a9bb0'],
    ['marksman', 'Ranging Mark', '#e8a33d'],
    ['arcanist', 'Runed Slab', '#9b5de5'],
    ['venom', 'Blighted Soil', '#8ac926'],
    ['frost', 'Rimeplate', '#4cc9f0'],
    ['tempest', 'Storm Rod', '#dbe9ff'],
    ['alchemist', 'Alembic Ring', '#ffd60a'],
    ['beacon', 'Lensing Dais', '#ff7ab0'],
    ['swarm', 'Hive Grate', '#6fe08a'],
    ['void', 'Null Sigil', '#ff006e'],
  ] as const
).map(([classId, name, color]) => ({
  id: `class-${classId}`,
  name,
  description: `The tower here also counts as ${classId[0]!.toUpperCase()}${classId.slice(1)}.`,
  color,
  // Individually rare, collectively the most common outcome — ten of them at weight 3
  // against the three below, so roughly two thirds of tiles grant a class.
  weight: 3,
  particle: 'zoneMote' as const,
  grantsClass: classId as ClassId,
}));

export const BUFF_TILES: BuffTileDef[] = [
  ...CLASS_TILES,
  {
    id: 'vantage',
    name: 'Vantage Point',
    description: 'The tower here reaches 1.5 tiles further.',
    color: '#c8c8d0',
    weight: 10,
    particle: 'beamMote',
    // Range is the one STAT worth putting on a tile: it changes coverage, which is a
    // positional property, so it alters what the rest of the board wants to do around it.
    rangeBonus: 1.5,
  },
  {
    id: 'emberbed',
    name: 'Emberbed',
    description: 'The tower here sets its targets alight.',
    color: '#ff6a3d',
    weight: 8,
    particle: 'ember',
    // Poison rather than a new status kind: burning is what it reads as, and the damage
    // pipeline already owns stacking, ticks and immunity for it.
    onHitStatus: { status: 'poison', magnitude: 1, durationTicks: 180 },
  },
  {
    id: 'heartspring',
    name: 'Heartspring',
    description: 'Restores 1 Core HP every second wave a tower stands here.',
    color: '#ff4d6d',
    weight: 6,
    particle: 'zoneMote',
    // The only payout here that is not combat, so the question stops being "which tower
    // is strongest on this tile" and becomes "is a life worth a tower in a poor spot".
    // Every SECOND wave, not every wave: the Core holds 20 and runs are 22–28 waves, so
    // one a wave would more than double the run's whole life total.
    coreHpEveryNWaves: 2,
  },
];

export const BUFF_TILES_BY_ID: Readonly<Record<string, BuffTileDef>> = Object.freeze(
  Object.fromEntries(BUFF_TILES.map((t) => [t.id, t])),
);
